<template>
  <v-off-canvas
    id="template-filters-offcanvas-v2"
    :backdrop-static="false"
    offcanvas-class="offcanvas-end"
    @closed="$emit('closed')"
  >
    <resizable-container>
      <off-canvas-header>
        <h5 class="offcanvas-title">{{ $t('general.shared.filters') }}</h5>
      </off-canvas-header>

      <off-canvas-body>
        <v-form
          id="template-filter-status-select"
          :label="$t('general.shared.status')"
          form-type="outline"
        >
          <v-select
            v-model="filterStatus"
            :clearable="false"
            :options="statusTypesOptions"
            :reduce="(returnValue) => returnValue.key"
            @option:selected="$emit('update:status', filterStatus); $emit('selected')"
            @update:modelValue="$emit('update:status', filterStatus); $emit('selected')"
          >
            <template v-slot:no-options>
              <div class="my-4">{{ $t('general.vueSelect.noOptions') }}</div>
            </template>
          </v-select>
        </v-form>

        <v-form
          id="template-filter-travel-type-select"
          :label="$t('general.shared.travelType')"
          form-type="outline"
        >
          <v-select
            v-model="filterTravelType"
            :clearable="false"
            :options="travelTypeOptions"
            :reduce="(returnValue) => returnValue.key"
            @option:selected="$emit('update:travelType', filterTravelType); $emit('selected')"
          >
            <template v-slot:no-options>
              <div class="my-4">{{ $t('general.vueSelect.noOptions') }}</div>
            </template>
          </v-select>
        </v-form>

        <v-form
          id="template-filter-modality-select"
          :label="$t('template.modality')"
          form-type="outline"
        >
          <v-select
            v-model="filterModality"
            :clearable="false"
            :options="modalityOptions"
            :reduce="(returnValue) => returnValue.key"
            @option:selected="$emit('update:modality', filterModality); $emit('selected')"
          >
            <template v-slot:no-options>
              <div class="my-4">{{ $t('general.vueSelect.noOptions') }}</div>
            </template>
          </v-select>
        </v-form>

        <v-form
          id="template-filter-cms-status-select"
          :label="$t('general.shared.cmsStatus')"
          form-type="outline"
        >
          <v-select
            v-model="filterCmsStatus"
            :clearable="false"
            :options="cmsStatusOptions"
            :reduce="(returnValue) => returnValue.key"
            @option:selected="$emit('update:cmsStatus', filterCmsStatus); $emit('selected')"
          >
            <template v-slot:no-options>
              <div class="my-4">{{ $t('general.vueSelect.noOptions') }}</div>
            </template>
          </v-select>
        </v-form>

        <v-form-input
          id="template-filter-total-days-select"
          v-model="filterTotalDays"
          :label="$t('general.shared.totalDays')"
          autocomplete="off"
          form-type="outline"
          type="number"
          @update:modelValue="$emit('update:totalDays', filterTotalDays); $emit('selected')"
        />

        <v-form-input
          id="template-filter-origin-select"
          v-model="filterOrigin"
          :label="$t('general.shared.origin')"
          autocomplete="off"
          form-type="outline"
          @update:modelValue="$emit('update:origin', filterOrigin); $emit('selected')"
        />

        <v-form-input
          id="template-filter-destination-select"
          v-model="filterDestination"
          :label="$t('general.shared.destination')"
          autocomplete="off"
          form-type="outline"
          @update:modelValue="$emit('update:destination', filterDestination); $emit('selected')"
        />

        <v-form-input
          id="template-filter-location-select"
          v-model="filterLocation"
          :label="$t('general.shared.location')"
          autocomplete="off"
          form-type="outline"
          @update:modelValue="$emit('update:location', filterLocation); $emit('selected')"
        />
      </off-canvas-body>
    </resizable-container>
  </v-off-canvas>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import vSelect from 'vue-select';
import { VForm, VFormInput } from '@uniqoders/form';
import VOffCanvas from '@/components/vendor/basic/offcanvas/VOffCanvas.vue';
import OffCanvasHeader from '@/components/vendor/basic/offcanvas/OffCanvasHeader.vue';
import OffCanvasBody from '@/components/vendor/basic/offcanvas/OffCanvasBody.vue';
import useConcepts from '@/helpers/Concepts';
import { mapConcepts } from '@/helpers/ConceptHelper';
import { buildModalitySelectOptions } from '@/helpers/ModalityHelper';
import ResizableContainer from '@/components/resizablecontainer/ResizableContainer.vue';

export default defineComponent({
  name: 'TheTemplateFiltersOffcanvasV2',
  components: {
    OffCanvasBody,
    OffCanvasHeader,
    VOffCanvas,
    vSelect,
    VForm,
    VFormInput,
    ResizableContainer,
  },
  emits: [
    'closed',
    'selected',
    'update:cmsStatus',
    'update:destination',
    'update:location',
    'update:modality',
    'update:origin',
    'update:status',
    'update:totalDays',
    'update:travelType',
  ],
  props: {
    cmsStatus: {
      type: String,
      required: false,
      default: '',
    },
    destination: {
      type: String,
      required: false,
      default: '',
    },
    location: {
      type: String,
      required: false,
      default: '',
    },
    modality: {
      type: String,
      required: false,
      default: '',
    },
    origin: {
      type: String,
      required: false,
      default: '',
    },
    status: {
      type: String,
      required: true,
      default: 'active',
    },
    totalDays: {
      type: [String, Number],
      required: false,
      default: '',
    },
    travelType: {
      type: String,
      required: false,
      default: '',
    },
  },
  setup() {
    return {
      ...useConcepts(),
    };
  },
  data() {
    return {
      filterCmsStatus: this.cmsStatus,
      filterDestination: this.destination,
      filterLocation: this.location,
      filterModality: this.modality,
      filterOrigin: this.origin,
      filterStatus: this.status,
      filterTotalDays: this.totalDays,
      filterTravelType: this.travelType,
    };
  },
  computed: {
    statusTypesOptions(): any {
      if (this.concepts && this.concepts['app.states']) {
        return mapConcepts(this.concepts['app.states']);
      }

      return [];
    },
    travelTypeOptions(): any {
      if (this.concepts && this.concepts['tour.travel_type']) {
        const concepts = mapConcepts(this.concepts['tour.travel_type']);

        concepts.unshift({
          key: '',
          label: this.$t('general.shared.all'),
          permission: null,
          value: null,
          order: 0,
          status: 'active',
          icon: '',
          color: '',
        });

        return concepts;
      }

      return [];
    },
    modalityOptions(): any[] {
      return [
        {
          key: '',
          label: this.$t('general.shared.all'),
        },
        ...buildModalitySelectOptions((key: string) => this.$t(key)),
      ];
    },
    cmsStatusOptions(): any[] {
      return [
        {
          key: '',
          label: this.$t('general.shared.all'),
        },
        {
          key: 'published',
          label: this.$t('cms.activities.cmsActivityPublished'),
        },
        {
          key: 'draft',
          label: this.$t('cms.activities.cmsActivityDraft'),
        },
        {
          key: 'none',
          label: this.$t('general.shared.cmsStatusNone'),
        },
      ];
    },
  },
  watch: {
    cmsStatus(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.filterCmsStatus = this.cmsStatus;
      }
    },
    destination(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.filterDestination = this.destination;
      }
    },
    location(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.filterLocation = this.location;
      }
    },
    modality(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.filterModality = this.modality;
      }
    },
    origin(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.filterOrigin = this.origin;
      }
    },
    status(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.filterStatus = this.status;
      }
    },
    totalDays(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.filterTotalDays = this.totalDays;
      }
    },
    travelType(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.filterTravelType = this.travelType;
      }
    },
  },
});
</script>
